import { useParams } from "react-router-dom";
import { useRef } from "react";

import { Card2 } from "@/ui2/atoms/Card";
import { cn } from "@/lib/helpers/cn";
import { Divider2 } from "@/ui2/atoms/Divider";
import { getAutomationDataAttr } from "@/utils/automationUtils";
import { Heading2 } from "@/ui2/atoms/Heading";
import { useIsSticky } from "@/lib/hooks/useIsSticky";

import { PatientAcuity } from "../(header-acuity)/PatientAcuity";
import { usePatientDetails } from "../api/queries/usePatientDetails";

import { PatientActions } from "./components/PatientActions";
import { PatientContact } from "./components/PatientContact";
import { PatientDetails } from "./components/PatientDetails";
import { PatientDiagnosis } from "./components/PatientDiagnosis";
import { PatientInsurance } from "./components/PatientInsurance";
import { PatientStatus } from "./components/PatientStatus";
import { PatientStatusDetails } from "./components/PatientStatusDetails";
import "./MemberProfileHeader.css";
import { ProfileTabs } from "./components/ProfileTabs";

export function MemberProfileHeader() {
  const { patientId } = useParams();
  const { data: patientDetails } = usePatientDetails({ patientId });
  const headerRef = useRef(null);
  const isSticky = useIsSticky(headerRef);

  if (!patientDetails) return null;

  return (
    <Card2
      as="header"
      className={cn(
        "tw:sticky tw:top-[calc(var(--header-height)+var(--omnisearch-height))] tw:z-10",
        "tw:flex-col tw:gap-x-4",
        "tw:max-md:p-4 tw:md:p-6 tw:lg:pb-0!",
        isSticky && "tw:rounded-t-none",
      )}
      ref={headerRef}
      {...getAutomationDataAttr("member-profile-header")}
    >
      <section
        className={cn(
          "tw:grid tw:justify-between",
          "tw:max-lg:justify-stretch tw:max-lg:gap-y-4",
          `tw:gap-4 tw:lg:grid-flow-col tw:lg:grid-cols-[1fr_min-content_var(--member-profile-header-column)]`,
        )}
      >
        <Heading2 as="h2" className="tw:sr-only">
          Patient overview
        </Heading2>

        <PatientDetails isSticky={isSticky} patientDetails={patientDetails} />
        <PatientActions patientDetails={patientDetails} />
        <PatientContact patientDetails={patientDetails} />
      </section>

      <div
        className={cn(
          "tw:grid tw:transition-[grid-template-rows,opacity] tw:duration-200 tw:ease-in-out",
          isSticky ? "tw:grid-rows-[0fr] tw:opacity-0" : "tw:grid-rows-[1fr] tw:opacity-100",
        )}
      >
        <section
          className={cn(
            isSticky && "tw:overflow-hidden",
            "tw:grid tw:justify-between tw:gap-x-4",
            "tw:max-lg:justify-stretch tw:max-lg:gap-y-4",
            `tw:lg:mt-2 tw:lg:grid-flow-col tw:lg:grid-cols-[var(--member-profile-header-column)_1fr_var(--member-profile-header-column)]`,
            "tw:border-t tw:border-stroke-quaternary",
          )}
        >
          <Heading2 as="h2" className="tw:sr-only">
            Clinical summary
          </Heading2>

          <div
            className="tw:flex tw:flex-col tw:gap-y-2 tw:pt-4"
            {...getAutomationDataAttr("member-profile-header-status")}
          >
            <PatientStatus patientDetails={patientDetails} />
            <PatientStatusDetails patientDetails={patientDetails} />
          </div>

          <PatientDiagnosis />
          <Divider2 className="tw:lg:hidden" />

          <div className="tw:flex tw:flex-col tw:gap-y-4 tw:lg:pt-4">
            <PatientAcuity patientDetails={patientDetails} patientId={patientId} />
            <PatientInsurance patientDetails={patientDetails} />
          </div>
        </section>
      </div>

      <section className="tw:border-t tw:border-stroke-quaternary tw:lg:pt-1">
        <Heading2 as="h2" className="tw:sr-only">
          Profile navigation
        </Heading2>

        <ProfileTabs />
      </section>
    </Card2>
  );
}
